import { useState, useEffect } from 'react';
import { Alert, Modal, Form, Button, Accordion, Spinner } from 'react-bootstrap';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import Attachments from 'components/Attachments/Attachments';
import { GET_CARD_UPDATES_FOR_CARD, useLazyQuery } from 'utils/graphql';
import { useTranslation } from 'react-i18next';

const HISTORY_PAGE_SIZE = 6;

const CardDetailModal = ({
    show,
    onHide,
    mode, // 'create' | 'detail'
    card, // existing card for detail, null for create
    onSubmit,
    isSubmitting,
    existingReasons,
    savedUpdateId, // set by parent after a successful update -- triggers the post-save view
}) => {
    const { t } = useTranslation();

    const formatDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '\u2014');
    const formatDateTime = (d) => {
        if (!d) return '\u2014';
        const dt = new Date(d);
        return `${dt.toISOString().slice(0, 10)} ${dt.toISOString().slice(11, 16)}`;
    };

    // Detail sub-view: 'detail' (default) | 'update' (new update form) | 'post-save' (attach files)
    const [detailView, setDetailView] = useState('detail');

    // Create form state
    const [cardNumber, setCardNumber] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [activationDate, setActivationDate] = useState('');
    const [cardType, setCardType] = useState('Regular');
    const [numCards, setNumCards] = useState('');
    const [initialValue, setInitialValue] = useState('');
    const [currentBalance, setCurrentBalance] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [reasonForActivation, setReasonForActivation] = useState('');
    const [notes, setNotes] = useState('');

    // Update form state
    const [numDistributed, setNumDistributed] = useState('');
    const [numDestroyed, setNumDestroyed] = useState('');
    const [numTransferred, setNumTransferred] = useState('');
    const [numLost, setNumLost] = useState('');
    const [givenTo, setGivenTo] = useState('');
    const [updateNotes, setUpdateNotes] = useState('');

    const [validated, setValidated] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Update logs
    const [updateLogs, setUpdateLogs] = useState([]);
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);

    const [getCardUpdates] = useLazyQuery(GET_CARD_UPDATES_FOR_CARD, {
        fetchPolicy: 'network-only',
    });

    // Computed: total face value at activation (read-only display only)
    const totalAtActivation = (parseFloat(numCards) || 0) * (parseFloat(initialValue) || 0);

    // Auto-fill currentBalance from num_cards x initial_value in create mode
    useEffect(() => {
        if (mode === 'create') {
            setCurrentBalance(totalAtActivation > 0 ? totalAtActivation.toFixed(2) : '');
        }
    }, [numCards, initialValue, mode, totalAtActivation]);

    // Load update logs when opening detail mode
    const loadUpdateLogs = async () => {
        if (!card?.id) return;
        setIsLoadingLogs(true);
        try {
            const { data: updatesData } = await getCardUpdates({
                variables: { card_id: card.id },
            });
            const logs = (updatesData?.table?.records || []).map((r) => {
                const entry = r.gc_card_inventory_updates;
                const updatedByName =
                    entry.updated_by_propertyuser__c_list?.[0]?.propertyuser__c?.name ||
                    t('deleted_user');
                return {
                    id: entry.id,
                    date: entry.updated_date,
                    updatedByName,
                    new_balance: entry.new_balance,
                    num_distributed: entry.num_distributed,
                    num_destroyed: entry.num_destroyed,
                    num_transferred: entry.num_transferred,
                    num_lost: entry.num_lost,
                    given_to: entry.given_to,
                    notes: entry.notes,
                };
            });
            setUpdateLogs(logs);
            setHistoryPage(1);
        } catch {
            // non-critical
        }
        setIsLoadingLogs(false);
    };

    useEffect(() => {
        if (show && mode === 'detail' && card?.id) {
            loadUpdateLogs();
        }
    }, [show, mode, card?.id]);

    // On save: reload logs, show success banner, return to detail view
    useEffect(() => {
        if (savedUpdateId && mode === 'detail') {
            setSaveSuccess(true);
            setDetailView('detail');
            setHistoryPage(1);
            loadUpdateLogs();
        }
    }, [savedUpdateId]);

    useEffect(() => {
        if (show) {
            if (mode === 'create') {
                resetCreateForm();
            }
            if (mode === 'detail') {
                setUpdateLogs([]);
                setIsLoadingLogs(true);
                setHistoryPage(1);
            }
            setDetailView('detail');
            resetUpdateForm();
            setSaveSuccess(false);
            setValidated(false);
            setFormErrors({});
        }
    }, [show, mode]);

    const resetCreateForm = () => {
        setCardNumber('');
        setSerialNumber('');
        setActivationDate('');
        setCardType('Regular');
        setNumCards('');
        setInitialValue('');
        setCurrentBalance('');
        setExpiryDate('');
        setReasonForActivation('');
        setNotes('');
    };

    const resetUpdateForm = () => {
        setNumDistributed('');
        setNumDestroyed('');
        setNumTransferred('');
        setNumLost('');
        setGivenTo('');
        setUpdateNotes('');
        setFormErrors({});
        setValidated(false);
    };

    const validateCreate = () => {
        const errors = {};
        if (!reasonForActivation) errors.reasonForActivation = 'reason_for_activation_empty_error';
        if (!cardNumber) errors.cardNumber = 'card_number_empty_error';
        if (!activationDate) errors.activationDate = 'activation_date_empty_error';
        if (!expiryDate) errors.expiryDate = 'expiry_date_empty_error';
        if (numCards === '') errors.numCards = 'num_cards_empty_error';
        if (initialValue === '') errors.initialValue = 'face_value_empty_error';
        if (currentBalance === '') errors.currentBalance = 'current_balance_empty_error';
        setFormErrors(errors);
        setValidated(true);
        return Object.keys(errors).length === 0;
    };

    const handleCreateSubmit = () => {
        if (!validateCreate()) return;
        onSubmit({
            card_number: cardNumber || null,
            serial_number: serialNumber || null,
            activation_date: activationDate || null,
            card_type: cardType || null,
            num_cards: numCards !== '' ? parseInt(numCards) : null,
            initial_value: initialValue !== '' ? parseFloat(initialValue) : null,
            current_balance: currentBalance !== '' ? parseFloat(currentBalance) : null,
            expiry_date: expiryDate || null,
            reason_for_activation: reasonForActivation || null,
            notes: notes || null,
        });
    };

    const handleUpdateSubmit = () => {
        const errors = {};
        if (calculatedOnHand != null && calculatedOnHand < 0)
            errors.calculatedOnHand = 'cards_on_hand_negative_error';
        if (hasBalance && numDistributed === '')
            errors.numDistributed = 'num_distributed_empty_error';
        if (hasBalance && !givenTo.trim()) errors.givenTo = 'given_to_empty_error';
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setValidated(true);
            return;
        }
        onSubmit({
            _isUpdate: true,
            new_balance: calculatedOnHand,
            num_distributed: numDistributed !== '' ? parseInt(numDistributed) : null,
            num_destroyed: numDestroyed !== '' ? parseInt(numDestroyed) : null,
            num_transferred: numTransferred !== '' ? parseInt(numTransferred) : null,
            num_lost: numLost !== '' ? parseInt(numLost) : null,
            given_to: givenTo.trim() || null,
            notes: updateNotes || null,
        });
    };

    const reasonOptions = existingReasons || [];

    const formatCurrency = (v) => (v != null ? `$${parseFloat(v).toFixed(2)}` : '\u2014');

    const latestOnHand = updateLogs.length > 0 ? updateLogs[0].new_balance : null;

    // On hand before this update: latest log, or the activation count if no logs yet
    const currentOnHand =
        latestOnHand != null
            ? parseInt(latestOnHand)
            : card?.num_cards != null
              ? card.num_cards
              : null;
    // Same balance as the inventory list's Current Balance column
    const currentBalanceValue =
        currentOnHand != null && card?.initial_value != null
            ? currentOnHand * card.initial_value
            : card?.current_balance;
    const hasBalance = currentBalanceValue > 0;

    // On hand is derived from what left inventory in this update
    const toCount = (v) => (v !== '' ? parseInt(v) || 0 : 0);
    const calculatedOnHand =
        currentOnHand != null
            ? currentOnHand -
              toCount(numDistributed) -
              toCount(numDestroyed) -
              toCount(numTransferred) -
              toCount(numLost)
            : null;
    const totalHistoryPages = Math.max(1, Math.ceil(updateLogs.length / HISTORY_PAGE_SIZE));
    const historyStart = (historyPage - 1) * HISTORY_PAGE_SIZE;
    const paginatedLogs = updateLogs.slice(historyStart, historyStart + HISTORY_PAGE_SIZE);

    // -- Detail mode: new update form --
    if (mode === 'detail' && detailView === 'update') {
        return (
            <Modal show={show} onHide={onHide} size="lg" className="card-detail-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>{t('new_update_log')}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="row">
                            <div className="col-md-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {hasBalance && <span className="required-field">*</span>}{' '}
                                        {t('num_distributed')}
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        isInvalid={validated && !!formErrors.numDistributed}
                                        value={numDistributed}
                                        onChange={(e) => setNumDistributed(e.target.value)}
                                    />
                                    {formErrors.numDistributed && (
                                        <Form.Control.Feedback type="invalid">
                                            {t(formErrors.numDistributed)}
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </div>
                            <div className="col-md-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('num_destroyed')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={numDestroyed}
                                        onChange={(e) => setNumDestroyed(e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('num_transferred')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={numTransferred}
                                        onChange={(e) => setNumTransferred(e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-3">
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('num_lost')}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={numLost}
                                        onChange={(e) => setNumLost(e.target.value)}
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                {hasBalance && <span className="required-field">*</span>}{' '}
                                {t('given_to')}
                            </Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={1024}
                                isInvalid={validated && !!formErrors.givenTo}
                                value={givenTo}
                                onChange={(e) => setGivenTo(e.target.value)}
                            />
                            {formErrors.givenTo && (
                                <Form.Control.Feedback type="invalid">
                                    {t(formErrors.givenTo)}
                                </Form.Control.Feedback>
                            )}
                            <Form.Text className="text-muted small">{t('given_to_help')}</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('cards_on_hand')}</Form.Label>
                            <div className="computed-field">
                                {calculatedOnHand != null ? calculatedOnHand : '—'}
                            </div>
                            {formErrors.calculatedOnHand && (
                                <p className="text-danger small mt-1 mb-0">
                                    {t(formErrors.calculatedOnHand)}
                                </p>
                            )}
                            <Form.Text className="text-muted small">
                                {t('cards_on_hand_help')}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('notes')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={updateNotes}
                                onChange={(e) => setUpdateNotes(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton onClick={handleUpdateSubmit} isLoading={isSubmitting}>
                        {t('save')}
                    </LoadingButton>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            resetUpdateForm();
                            setDetailView('detail');
                        }}
                    >
                        {t('cancel')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // -- Detail mode: full item details + update log timeline --
    if (mode === 'detail' && card) {
        const expired =
            !!card.expiry_date &&
            card.expiry_date.slice(0, 10) < new Date().toISOString().slice(0, 10);
        return (
            <Modal show={show} onHide={onHide} size="lg" className="card-detail-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>{t('card_details')}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {saveSuccess && (
                        <Alert
                            variant="success"
                            dismissible
                            onClose={() => setSaveSuccess(false)}
                            className="mb-3"
                        >
                            {t('update_saved')}
                        </Alert>
                    )}
                    {expired && (
                        <Alert variant="warning" className="mb-3">
                            {card.card_type === 'Promotion'
                                ? t('card_expired_promotion_warning')
                                : t('card_expired_regular_warning')}
                        </Alert>
                    )}
                    {/* Card info summary */}
                    {isLoadingLogs ? (
                        <div className="d-flex justify-content-center align-items-center my-4">
                            <Spinner />
                        </div>
                    ) : (
                        <div className="card-summary mb-3 p-3 bg-light rounded">
                            <div className="row">
                                <div className="col-md-6">
                                    <small className="text-muted">{t('card_number')}</small>
                                    <div>{card.card_number || '\u2014'}</div>
                                </div>
                                <div className="col-md-6">
                                    <small className="text-muted">{t('serial_number')}</small>
                                    <div>{card.serial_number || '\u2014'}</div>
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <small className="text-muted">{t('card_type')}</small>
                                    <div>{card.card_type || '\u2014'}</div>
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <small className="text-muted">{t('num_cards')}</small>
                                    <div>{card.num_cards != null ? card.num_cards : '\u2014'}</div>
                                </div>
                                <div className="col-md-6">
                                    <small className="text-muted">{t('cards_on_hand')}</small>
                                    <div>
                                        {latestOnHand != null
                                            ? latestOnHand
                                            : card.num_cards != null
                                              ? card.num_cards
                                              : '\u2014'}
                                    </div>
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <small className="text-muted">{t('face_value_per_card')}</small>
                                    <div>{formatCurrency(card.initial_value)}</div>
                                </div>
                                <div className="col-md-6">
                                    <small className="text-muted">{t('activation_total')}</small>
                                    <div>
                                        {formatCurrency(
                                            (card.num_cards || 0) * (card.initial_value || 0) ||
                                                null
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <small className="text-muted">{t('current_balance')}</small>
                                    <div>{formatCurrency(currentBalanceValue)}</div>
                                </div>
                            </div>
                            <div className="row mt-2">
                                <div className="col-md-6">
                                    <small className="text-muted">{t('activation_date')}</small>
                                    <div>{formatDate(card.activation_date)}</div>
                                </div>
                                <div className="col-md-6">
                                    <small className="text-muted">{t('expiry_date')}</small>
                                    <div>{formatDate(card.expiry_date)}</div>
                                </div>
                            </div>
                            {card.reason_for_activation && (
                                <div className="row mt-2">
                                    <div className="col-md-6">
                                        <small className="text-muted">
                                            {t('reason_for_activation')}
                                        </small>
                                        <div>{card.reason_for_activation}</div>
                                    </div>
                                </div>
                            )}
                            {card.notes && (
                                <div className="row mt-2">
                                    <div className="col-12">
                                        <small className="text-muted">{t('notes')}</small>
                                        <div>{card.notes}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* New Update button */}
                    <Button
                        className="w-100 mb-3"
                        onClick={() => {
                            setSaveSuccess(false);
                            resetUpdateForm();
                            setDetailView('update');
                        }}
                    >
                        {t('new_update_log')}
                    </Button>

                    {/* Update log timeline */}
                    <h6 className="mb-2">{t('update_history')}</h6>
                    {isLoadingLogs && (
                        <div className="text-center my-3">
                            <div className="spinner-border spinner-border-sm" role="status" />
                        </div>
                    )}
                    {!isLoadingLogs && updateLogs.length === 0 && (
                        <p className="text-muted">{t('no_history')}</p>
                    )}
                    <Accordion>
                        {paginatedLogs.map((log) => (
                            <Accordion.Item eventKey={`log-${log.id}`} key={log.id}>
                                <Accordion.Header>
                                    <div>
                                        <strong>{t('balance_update')}</strong>
                                        <span className="text-muted small ms-2">
                                            {formatDateTime(log.date)}
                                        </span>
                                        <span className="text-muted small ms-2">
                                            {t('by')} {log.updatedByName}
                                        </span>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="row mb-2">
                                        <div className="col-md-6">
                                            <small className="text-muted">
                                                {t('cards_on_hand')}
                                            </small>
                                            <div>
                                                {log.new_balance != null ? log.new_balance : '—'}
                                            </div>
                                        </div>
                                    </div>
                                    {(log.num_distributed ||
                                        log.num_destroyed ||
                                        log.num_transferred ||
                                        log.num_lost) && (
                                        <div className="row mb-2">
                                            {log.num_distributed != null &&
                                                parseInt(log.num_distributed) > 0 && (
                                                    <div className="col-md-3">
                                                        <small className="text-muted">
                                                            {t('distributed')}
                                                        </small>
                                                        <div>{log.num_distributed}</div>
                                                    </div>
                                                )}
                                            {log.num_destroyed != null &&
                                                parseInt(log.num_destroyed) > 0 && (
                                                    <div className="col-md-3">
                                                        <small className="text-muted">
                                                            {t('destroyed')}
                                                        </small>
                                                        <div>{log.num_destroyed}</div>
                                                    </div>
                                                )}
                                            {log.num_transferred != null &&
                                                parseInt(log.num_transferred) > 0 && (
                                                    <div className="col-md-3">
                                                        <small className="text-muted">
                                                            {t('transferred')}
                                                        </small>
                                                        <div>{log.num_transferred}</div>
                                                    </div>
                                                )}
                                            {log.num_lost != null && parseInt(log.num_lost) > 0 && (
                                                <div className="col-md-3">
                                                    <small className="text-muted">
                                                        {t('lost')}
                                                    </small>
                                                    <div>{log.num_lost}</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {log.given_to && (
                                        <div className="mb-2">
                                            <small className="text-muted">{t('given_to')}</small>
                                            <div>{log.given_to}</div>
                                        </div>
                                    )}
                                    {log.notes && (
                                        <div className="mb-2">
                                            <small className="text-muted">{t('notes')}</small>
                                            <div>{log.notes}</div>
                                        </div>
                                    )}
                                    <Attachments
                                        parentId={log.id}
                                        parentTable="gc_card_inventory_updates"
                                        readOnly={false}
                                    />
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                    {!isLoadingLogs && updateLogs.length > HISTORY_PAGE_SIZE && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={historyPage <= 1}
                                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                            >
                                {'<'}
                            </Button>
                            <small className="text-muted">
                                {historyPage} / {totalHistoryPages}
                            </small>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={historyPage >= totalHistoryPages}
                                onClick={() =>
                                    setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))
                                }
                            >
                                {'>'}
                            </Button>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        {t('close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // -- Create mode --
    return (
        <Modal show={show} onHide={onHide} size="lg" className="card-detail-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    <h5>{t('create_card')}</h5>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    {/* Row 1: Reason for Activation | Card Type */}
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span>{' '}
                                    {t('reason_for_activation')}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    list="card-reason-suggestions"
                                    isInvalid={validated && !!formErrors.reasonForActivation}
                                    value={reasonForActivation}
                                    onChange={(e) => setReasonForActivation(e.target.value)}
                                    placeholder={t('enter_custom_reason')}
                                />
                                <datalist id="card-reason-suggestions">
                                    {reasonOptions.map((r, i) => (
                                        <option key={i} value={r} />
                                    ))}
                                </datalist>
                                {formErrors.reasonForActivation && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.reasonForActivation)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('reason_for_activation_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('card_type')}
                                </Form.Label>
                                <Form.Select
                                    value={cardType}
                                    onChange={(e) => setCardType(e.target.value)}
                                >
                                    <option value="Regular">{t('card_type_regular')}</option>
                                    <option value="Promotion">{t('card_type_promotion')}</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>

                    {/* Row 2: Card Number | Serial Number */}
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('card_number')}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    isInvalid={validated && !!formErrors.cardNumber}
                                    value={cardNumber}
                                    onChange={(e) => setCardNumber(e.target.value)}
                                />
                                {formErrors.cardNumber && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.cardNumber)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('card_number_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>{t('serial_number')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={serialNumber}
                                    onChange={(e) => setSerialNumber(e.target.value)}
                                />
                                <Form.Text className="text-muted small">
                                    {t('serial_number_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </div>

                    {/* Row 3: Activation Date (required) | Expiry Date */}
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('activation_date')}
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    isInvalid={validated && !!formErrors.activationDate}
                                    value={activationDate}
                                    onChange={(e) => setActivationDate(e.target.value)}
                                />
                                {formErrors.activationDate && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.activationDate)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('activation_date_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('expiry_date')}
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    isInvalid={validated && !!formErrors.expiryDate}
                                    min={activationDate || ''}
                                    value={expiryDate}
                                    onChange={(e) => setExpiryDate(e.target.value)}
                                />
                                {formErrors.expiryDate && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.expiryDate)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('expiry_date_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </div>

                    {/* Row 4: # of Cards | Face Value per Card */}
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('num_cards')}
                                </Form.Label>
                                <Form.Control
                                    isInvalid={validated && !!formErrors.numCards}
                                    type="number"
                                    min="0"
                                    value={numCards}
                                    onChange={(e) => setNumCards(e.target.value)}
                                />
                                {formErrors.numCards && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.numCards)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('num_cards_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span>{' '}
                                    {t('face_value_per_card')}
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    isInvalid={validated && !!formErrors.initialValue}
                                    value={initialValue}
                                    onChange={(e) => setInitialValue(e.target.value)}
                                />
                                {formErrors.initialValue && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.initialValue)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('face_value_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </div>

                    {/* Row 5: Activation Total (read-only) | Current Balance */}
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span>{' '}
                                    {t('activation_total')}
                                </Form.Label>
                                <div className="computed-field">
                                    ${totalAtActivation.toFixed(2)}
                                </div>
                                <Form.Text className="text-muted small">
                                    {t('activation_total_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('current_balance')}
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    isInvalid={validated && !!formErrors.currentBalance}
                                    value={currentBalance}
                                    onChange={(e) => setCurrentBalance(e.target.value)}
                                />
                                {formErrors.currentBalance && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.currentBalance)}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted small">
                                    {t('current_balance_help')}
                                </Form.Text>
                            </Form.Group>
                        </div>
                    </div>

                    {/* Notes */}
                    <Form.Group className="mb-3">
                        <Form.Label>{t('notes')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                        <Form.Text className="text-muted small">{t('notes_help')}</Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <LoadingButton onClick={handleCreateSubmit} isLoading={isSubmitting}>
                    {t('create')}
                </LoadingButton>
                <Button variant="secondary" onClick={onHide}>
                    {t('cancel')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CardDetailModal;
